<!--
  Storico Parziali — lista ordini storici parziali.
  Redesigned 2026-06-15 (v2.8.0, Phase 1.5 Chunk G.1).

  Drops kendo-grid + 2× kendo-datepicker + kendo-dropdownlist +
  mat-spinner + ~40 inline `style="..."` attributes + il blocco
  "Legenda" accordion (sostituito da una chip viola persistente).

  Pattern coerente con bordero / ordini-da-firmare:
    - Sticky header con titolo + chip viola "Parziale" (brand color)
    - Filter bar nativa (search + date range + select cliente)
    - Table-card Diakonix con padding 14×16
    - Row click → openStorico() → modale storico-ordini-dettagli
  Status state per riga (left-border colorato): ok / progress /
  closed / danger / default — derivato da tab_order_status_cod.
-->

<div class="storico-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="storico-header">
    <div class="storico-header__titles">
      <h1 class="storico-header__title">
        ⏳ Storico ordini parziali
        <span class="status-chip status-chip--partial" title="Tutti gli ordini in questa lista hanno consegna parziale">
          Parziale
        </span>
      </h1>
      <p class="storico-header__subtitle">
        @if (filteredOrders.length > 0) {
          <strong>{{ filteredOrders.length }}</strong>
          {{ filteredOrders.length === 1 ? 'ordine parziale chiuso' : 'ordini parziali chiusi' }}
          @if (selectedCustomer || filterTerm) {
            · filtri attivi
          }
        } @else if (loading) {
          Caricamento storico…
        } @else {
          Nessun ordine parziale nello storico
        }
      </p>
    </div>

    <button
      type="button"
      class="refresh-btn"
      (click)="searchOrders()"
      title="Aggiorna lista"
      aria-label="Aggiorna lista"
    >🔁</button>
  </header>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="storico-filters" aria-label="Filtri ricerca">

    <!-- Search -->
    <div class="filter-field">
      <label class="filter-field__label" for="searchStorico">Cerca ordine</label>
      <div class="filter-field__input-wrap">
        <input
          id="searchStorico"
          type="search"
          class="filter-field__input"
          placeholder="N° ordine o cliente…"
          [(ngModel)]="filterTerm"
          (keyup.enter)="searchOrders()"
        />
        @if (filterTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="clearSearch()"
            aria-label="Rimuovi filtro"
          >×</button>
        }
      </div>
    </div>

    <!-- Date range -->
    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Data ordine</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.startingDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onStartDateChange($event)"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.endDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onEndDateChange($event)"
          aria-label="Data a"
        />
      </div>
    </div>

    <!-- Customer dropdown -->
    <div class="filter-field">
      <label class="filter-field__label" for="filterCustomer">Cliente</label>
      <select
        id="filterCustomer"
        class="filter-field__input filter-field__input--select"
        [(ngModel)]="selectedCustomer"
        (ngModelChange)="onCustomerChange($event)"
      >
        @for (cust of customers; track cust) {
          <option [ngValue]="cust === showAll ? null : cust">
            {{ cust || '— non specificato —' }}
          </option>
        }
      </select>
    </div>

    <button
      type="button"
      class="btn btn--primary"
      (click)="searchOrders()"
      [disabled]="loading"
    >🔍 Cerca</button>
  </section>

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="storico-table-card" aria-label="Elenco ordini parziali">

    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (filteredOrders.length === 0) {
      <div class="storico-empty">
        <div class="storico-empty__icon" aria-hidden="true">
          @if (filterTerm || selectedCustomer) { 🔎 } @else { 📭 }
        </div>
        <p class="storico-empty__msg">
          @if (filterTerm || selectedCustomer) {
            Nessun ordine parziale corrisponde ai filtri attivi.
          } @else {
            Nessun ordine parziale nello storico al momento.
          }
        </p>
      </div>
    } @else {
      <div class="storico-table-scroll">
        <table class="storico-table" role="table">
          <thead>
            <tr>
              <th class="col-order is-sortable" (click)="sortBy('or_order_number')">
                N° ordine
                @if (sortColumn === 'or_order_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-operator is-sortable" (click)="sortBy('ac_first_name')">
                Operatore
                @if (sortColumn === 'ac_first_name') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-customer is-sortable" (click)="sortBy('or_customer')">
                Cliente
                @if (sortColumn === 'or_customer') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('or_order_date')">
                Data ordine
                @if (sortColumn === 'or_order_date') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('or_closed_date')">
                Data chiusura
                @if (sortColumn === 'or_closed_date') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-num is-sortable" (click)="sortBy('op_articles_number')">
                N° pezzi
                @if (sortColumn === 'op_articles_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-num is-sortable" (click)="sortBy('or_net_value')">
                Importo
                @if (sortColumn === 'or_net_value') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            @for (
              item of filteredOrders;
              track item.order_id + '-' + ($index)
            ) {
              <tr
                class="storico-row"
                [attr.data-status]="statusState(item)"
                (click)="goToDetails(item.order_id)"
                title="Apri dettagli ordine"
              >
                <td class="col-order">
                  <span class="cell-order-num">{{ item.or_order_number || '—' }}</span>
                </td>
                <td class="col-operator">
                  <span class="cell-operator">
                    @if (item.ac_first_name || item.ac_last_name) {
                      {{ item.ac_first_name }} {{ item.ac_last_name }}
                    } @else { — }
                  </span>
                </td>
                <td class="col-customer">
                  <span class="cell-customer">{{ item.or_customer || '—' }}</span>
                </td>
                <td class="col-date">
                  <span class="cell-date">{{ formatDateIt(item.or_order_date) }}</span>
                </td>
                <td class="col-date">
                  <span class="cell-date">{{ formatDateIt(item.or_closed_date) }}</span>
                </td>
                <td class="col-num">
                  <span class="cell-num">{{ item.op_articles_number ?? 0 }}</span>
                </td>
                <td class="col-num">
                 <span class="cell-num">{{ item.or_net_value ?? 0 | currency:'EUR':'symbol' }}</span>
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  <button
                    type="button"
                    class="icon-btn"
                    title="Apri dettagli"
                    (click)="goToDetails(item.order_id)"
                  >ℹ️</button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

</div>

<!-- ─── Sub-modale dettagli (preserved, verrà restyled in v2.9.0) ── -->
@if (closeSrv.isStorico) {
  <app-storico-ordini-dettagli
    [opened]="closeSrv.isStorico"
    [order_id]="clickedOrder"
  ></app-storico-ordini-dettagli>
}
